import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import "./App.css";
import { createProduct, fetchProducts, updateProduct } from "./api/products";
import { fetchSuppliers, createSupplier, updateSupplier, archiveSupplier, restoreSupplier } from "./api/suppliers";
import { fetchCustomers, createCustomer, updateCustomer } from "./api/customers";
import { login as loginApi } from "./api/auth";
import {
  fetchInvoices,
  createInvoice,
  fetchInvoice,
  updateInvoice,
  recordInvoicePayment,
  logInvoiceEvent,
} from "./api/invoices";
import { NAV_ITEMS, SCREEN_CONFIG, SCREEN_META } from "./config/uiConfig";
import BlankState from "./components/BlankState";
import Icon from "./components/Icon";
import LoginScreen from "./components/LoginScreen";
import HomePage from "./pages/HomePage";
import InventoryPage from "./pages/InventoryPage";
import InvoicePage from "./pages/InvoicePage";
import InvoiceBuilderPage from "./pages/InvoiceBuilderPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import EmployeesPage from "./pages/EmployeesPage";
import SuppliersPage from "./pages/SuppliersPage";
import CustomersPage from "./pages/CustomersPage";
import CategoriesPage from "./pages/CategoriesPage";
import PrintBarcodesPage from "./pages/PrintBarcodesPage";
import CreateModal from "./components/CreateModal";
import ProductDrawer from "./components/ProductDrawer";
import SupplierDrawer from "./components/SupplierDrawer";
import CustomerDrawer from "./components/CustomerDrawer";
import BarcodeModal from "./components/BarcodeModal";
import ProductImportPage from "./pages/ProductImportPage";
import highWindowLogo from "./assets/HighWindow_logo.png";
import useSignedAssetUrls from "./hooks/useSignedAssetUrls";
import { normalizeAssetKeys, resolveAssetUrl } from "./utils/assets";

const DEFAULT_LOGIN_EMAIL = process.env.REACT_APP_DEFAULT_LOGIN_EMAIL || "";

const ACTIVE_TAB_STORAGE_KEY = "hwms-active-tab";
const getValidNavKey = (key) => (NAV_ITEMS.some((item) => item.key === key) ? key : null);
const parseActiveKeyFromLocation = () => {
  try {
    const url = new URL(window.location.href);
    const tabFromQuery = url.searchParams.get("tab");
    if (tabFromQuery && getValidNavKey(tabFromQuery)) return tabFromQuery;
  } catch (err) {
    console.error("Unable to read active tab from URL", err);
  }
  return null;
};

function App() {
  const [session, setSession] = useState(() => {
    try {
      const saved = localStorage.getItem("hwms-auth");
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      return parsed ? { ...parsed, remember: parsed.remember ?? true } : null;
    } catch (err) {
      console.error("Unable to read session", err);
      return null;
    }
  });
  const [authError, setAuthError] = useState("");
  const [activeKey, setActiveKey] = useState(() => {
    try {
      const urlActiveKey = parseActiveKeyFromLocation();
      if (urlActiveKey) return urlActiveKey;
      const stored = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
      if (stored && getValidNavKey(stored)) return stored;
    } catch (err) {
      console.error("Unable to read active tab", err);
    }
    return "home";
  });
  const [authLoading, setAuthLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCreateKey, setSelectedCreateKey] = useState(null);
  const [showProductDrawer, setShowProductDrawer] = useState(false);
  const [showImportScreen, setShowImportScreen] = useState(false);
  const [productSaving, setProductSaving] = useState(false);
  const [productFormError, setProductFormError] = useState("");
  const [showSupplierDrawer, setShowSupplierDrawer] = useState(false);
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [supplierFormError, setSupplierFormError] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierDrawerMode, setSupplierDrawerMode] = useState("create");
  const [supplierDrawerContext, setSupplierDrawerContext] = useState("default");
  const [supplierLinking, setSupplierLinking] = useState(false);
  const [supplierLinkError, setSupplierLinkError] = useState("");
  const [supplierStatusChanging, setSupplierStatusChanging] = useState(false);
  const [settingsDrawerSignal, setSettingsDrawerSignal] = useState(0);
  const [employeesDrawerSignal, setEmployeesDrawerSignal] = useState(0);
  const [showCustomerDrawer, setShowCustomerDrawer] = useState(false);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [customerFormError, setCustomerFormError] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDrawerMode, setCustomerDrawerMode] = useState("create");
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [barcodeSelection, setBarcodeSelection] = useState([]);
  const [toast, setToast] = useState({ message: "", tone: "success" });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [drawerMode, setDrawerMode] = useState("create");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const prevSupplierSaving = useRef(false);
  const [inventoryState, setInventoryState] = useState({
    rows: [],
    loading: false,
    error: "",
    source: "idle",
  });
  const inventoryImageKeys = useMemo(
    () =>
      (Array.isArray(inventoryState.rows) ? inventoryState.rows : [])
        .map((row) => row?.image?.key)
        .filter(Boolean),
    [inventoryState.rows]
  );
  const signedInventoryUrls = useSignedAssetUrls(inventoryImageKeys);
  const [suppliersState, setSuppliersState] = useState({
    rows: [],
    loading: false,
    error: "",
    source: "idle",
  });
  const [customersState, setCustomersState] = useState({
    rows: [],
    loading: false,
    error: "",
    source: "idle",
  });
  const [invoicesState, setInvoicesState] = useState({
    rows: [],
    loading: false,
    error: "",
    source: "idle",
  });
  const [invoiceMode, setInvoiceMode] = useState("list");
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [invoiceFormError, setInvoiceFormError] = useState("");
  const [lastSavedInvoice, setLastSavedInvoice] = useState(null);
  const [invoiceBuilderLoading, setInvoiceBuilderLoading] = useState(false);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const clearToast = useCallback(() => setToast((prev) => ({ ...prev, message: "" })), []);
  const notify = useCallback(
    (message, tone = "success") => setToast({ message, tone }),
    []
  );

  useEffect(() => {
    try {
      if (session?.remember) {
        localStorage.setItem("hwms-auth", JSON.stringify(session));
      } else {
        localStorage.removeItem("hwms-auth");
      }
    } catch (err) {
      console.error("Unable to persist session", err);
    }
  }, [session]);
  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeKey);
    } catch (err) {
      console.error("Unable to persist active tab", err);
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("tab", activeKey);
      window.history.replaceState(null, "", url.toString());
    } catch (err) {
      console.error("Unable to sync active tab to URL", err);
    }
  }, [activeKey]);
  useEffect(() => {
    const handlePopState = () => {
      const urlActiveKey = parseActiveKeyFromLocation();
      if (urlActiveKey && urlActiveKey !== activeKey) {
        setActiveKey(urlActiveKey);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [activeKey]);

  useEffect(() => {
    if (!toast?.message) return undefined;
    const id = setTimeout(() => clearToast(), 3500);
    return () => clearTimeout(id);
  }, [toast, clearToast]);

  const printMode = useMemo(() => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get("print");
    } catch (_err) {
      return null;
    }
  }, []);

  const handleLogin = useCallback(
    async (email, password, options = {}) => {
      const normalizedEmail = email.trim().toLowerCase();
      const remember = options.remember ?? true;
      setAuthError("");
      setAuthLoading(true);

      try {
        const response = await loginApi({ email: normalizedEmail, password, remember });
        const nextSession = {
          email: response?.email || normalizedEmail,
          role: response?.role || "Super Admin",
          signedInAt: response?.signedInAt || new Date().toISOString(),
          remember: response?.remember ?? remember,
          eligibility: response?.eligibility || [],
        };
        setSession(nextSession);
        return true;
      } catch (err) {
        console.error("Login failed via API", err);
        const status = err?.status;
        let message = err?.message || "Unable to login via API.";
        if (status === 401) {
          message = "Email or password is incorrect.";
        } else if (status === 403 && !err?.message) {
          message = "Your account is inactive. Contact an admin.";
        }
        setAuthError(message);
        return false;
      } finally {
        setAuthLoading(false);
      }
    },
    []
  );

  const handleLogout = useCallback((reasonOrEvent = "") => {
    const message = typeof reasonOrEvent === "string" ? reasonOrEvent : "";
    setSession(null);
    setActiveKey("home");
    setAuthError(message);
  }, []);

  const resetProductFlow = useCallback(({ preserveProduct = false } = {}) => {
    setProductSaving(false);
    setProductFormError("");
    if (!preserveProduct) {
      setSelectedProduct(null);
      setDrawerMode("create");
    }
  }, []);

  const resetSupplierFlow = useCallback(({ preserveSupplier = false } = {}) => {
    setSupplierSaving(false);
    setSupplierFormError("");
    setSupplierLinkError("");
    setSupplierLinking(false);
    setSupplierStatusChanging(false);
    if (!preserveSupplier) {
      setSelectedSupplier(null);
      setSupplierDrawerMode("create");
    }
    setSupplierDrawerContext("default");
  }, []);

  const resetCustomerFlow = useCallback(({ preserveCustomer = false } = {}) => {
    setCustomerSaving(false);
    setCustomerFormError("");
    if (!preserveCustomer) {
      setSelectedCustomer(null);
      setCustomerDrawerMode("create");
    }
  }, []);

  const resetInvoiceFlow = useCallback(() => {
    setInvoiceSaving(false);
    setInvoiceFormError("");
    setLastSavedInvoice(null);
    setInvoiceBuilderLoading(false);
    setEditingInvoiceId(null);
  }, []);

  const startInvoiceCreation = useCallback(() => {
    setActiveKey("invoice");
    resetInvoiceFlow();
    setInvoiceMode("create");
    setShowCreateModal(false);
    setSelectedCreateKey(null);
    setShowProductDrawer(false);
    setShowSupplierDrawer(false);
    setShowCustomerDrawer(false);
    setShowImportScreen(false);
  }, [resetInvoiceFlow]);

  const openProductDrawer = ({ withCreateModal = false } = {}) => {
    resetProductFlow();
    setDrawerMode("create");
    setSelectedProduct(null);
    setSelectedCreateKey("inventory");
    setShowProductDrawer(true);
    setShowSupplierDrawer(false);
    setShowCustomerDrawer(false);
    setShowImportScreen(false);
    setShowCreateModal(withCreateModal);
  };

  const openSupplierDrawer = ({ withCreateModal = false, source = "default", prefillName = "" } = {}) => {
    resetSupplierFlow();
    setSupplierDrawerMode("create");
    if (prefillName?.trim()) {
      setSelectedSupplier({ name: prefillName.trim() });
    } else {
      setSelectedSupplier(null);
    }
    if (source === "import") {
      setSelectedCreateKey(null);
    } else {
      setSelectedCreateKey("supplier");
      setShowImportScreen(false);
    }
    setShowSupplierDrawer(true);
    setShowProductDrawer(false);
    setShowCustomerDrawer(false);
    setShowCreateModal(withCreateModal);
    setSupplierDrawerContext(source);
  };

  const openCustomerDrawer = ({ withCreateModal = false } = {}) => {
    resetCustomerFlow();
    setCustomerDrawerMode("create");
    setSelectedCustomer(null);
    setSelectedCreateKey("customer");
    setShowCustomerDrawer(true);
    setShowProductDrawer(false);
    setShowSupplierDrawer(false);
    setShowImportScreen(false);
    setShowCreateModal(withCreateModal);
  };

  const closeSupplierDrawerOnly = useCallback(() => {
    resetSupplierFlow();
    setShowSupplierDrawer(false);
    setSupplierDrawerContext("default");
  }, [resetSupplierFlow]);

  const handleCloseCreate = useCallback(() => {
    setShowCreateModal(false);
    setShowProductDrawer(false);
    setShowSupplierDrawer(false);
    setShowCustomerDrawer(false);
    setShowImportScreen(false);
    setSelectedCreateKey(null);
    resetProductFlow();
    resetSupplierFlow();
    resetCustomerFlow();
  }, [resetCustomerFlow, resetProductFlow, resetSupplierFlow]);

  const handleCloseSupplierDrawer = useCallback(() => {
    if (supplierDrawerContext === "import") {
      closeSupplierDrawerOnly();
    } else {
      handleCloseCreate();
    }
  }, [closeSupplierDrawerOnly, handleCloseCreate, supplierDrawerContext]);

  useEffect(() => {
    if (prevSupplierSaving.current && !supplierSaving && !supplierFormError && !supplierLinkError && showSupplierDrawer) {
      handleCloseSupplierDrawer();
    }
    prevSupplierSaving.current = supplierSaving;
  }, [handleCloseSupplierDrawer, showSupplierDrawer, supplierFormError, supplierLinkError, supplierSaving]);

  const handleImportSupplierRequest = (supplierName = "") => {
    const cleanName = supplierName?.trim() || "";
    openSupplierDrawer({ source: "import", prefillName: cleanName });
  };

  const handleSelectCreate = (key) => {
    if (key === "inventory") {
      openProductDrawer({ withCreateModal: false });
      return;
    }

    if (key === "supplier") {
      openSupplierDrawer({ withCreateModal: false });
      return;
    }

    if (key === "customer") {
      openCustomerDrawer({ withCreateModal: false });
      return;
    }

    if (key === "invoice") {
      startInvoiceCreation();
      return;
    }

    if (key === "import") {
      setSelectedCreateKey(key);
      setShowImportScreen(true);
      setShowCreateModal(false);
      setShowProductDrawer(false);
      setShowSupplierDrawer(false);
      setShowCustomerDrawer(false);
      return;
    }

    setSelectedCreateKey(key);
    setShowProductDrawer(false);
    setShowSupplierDrawer(false);
    setShowCustomerDrawer(false);
    setShowImportScreen(key === "import");
  };

  const buildProductPayload = (form) => {
    const categoryIds = Array.isArray(form?.categories)
      ? form.categories
          .map((cat) => (typeof cat === "string" ? cat : cat?.id))
          .filter(Boolean)
          .map((id) => id.trim())
      : [];
    const categoryId = form?.categoryId?.trim?.() || categoryIds[0];

    const imageKeys = Array.isArray(form?.imageKeys) ? form.imageKeys.filter(Boolean) : [];
    const imageUrl = typeof form?.imageUrl === "string" ? form.imageUrl.trim() : undefined;

    return {
      refCode: form?.refCode?.trim().replace(/\s+/g, "") || form?.sku?.trim().replace(/\s+/g, ""),
      name: form?.name?.trim(),
      barcode: form?.barcode?.trim().replace(/\s+/g, ""),
      description: form?.description?.trim(),
      zone: form?.zone?.trim(),
      imageKeys,
      imageUrl,
      defaultPrice: form?.defaultPrice !== "" && form?.defaultPrice !== undefined ? Number(form.defaultPrice) : undefined,
      reorderLevel: form?.reorderLevel !== "" && form?.reorderLevel !== undefined ? Number(form.reorderLevel) : 0,
      categoryId,
      categoryIds,
      supplierId: form?.supplierId?.trim(),
      quantityOnHand: form?.quantity !== "" && form?.quantity !== undefined ? Number(form.quantity) : 0,
      status: form?.status,
      expiryDate: form?.expiryDate || undefined,
      origin: form?.origin?.trim(),
      costPrice: form?.costPrice !== "" && form?.costPrice !== undefined ? Number(form.costPrice) : undefined,
    };
  };

  const buildSupplierPayload = (form) => {
    const phone = form?.phone?.trim();
    const email = form?.email?.trim();
    const payload = {
      name: form?.name?.trim(),
      address: form?.address?.trim(),
      notes: form?.notes?.trim(),
    };

    if (phone || email) {
      payload.contact = {
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
      };
    }

    return payload;
  };

  const buildCustomerPayload = (form) => {
    const phone = form?.phone?.trim();
    const email = form?.email?.trim();
    const payload = {
      name: form?.name?.trim(),
      address: form?.address?.trim(),
      notes: form?.notes?.trim(),
    };

    if (phone || email) {
      payload.contact = {
        ...(phone ? { phone } : {}),
        ...(email ? { email } : {}),
      };
    }

    return payload;
  };

  const handleCreateSubmit = () => {
    if (selectedCreateKey === "inventory") {
      setShowProductDrawer(true);
      resetProductFlow();
      return;
    }

    if (selectedCreateKey === "supplier") {
      openSupplierDrawer({ withCreateModal: false });
      return;
    }

    if (selectedCreateKey === "customer") {
      openCustomerDrawer({ withCreateModal: false });
      return;
    }

    if (selectedCreateKey === "invoice") {
      startInvoiceCreation();
      return;
    }

    if (selectedCreateKey === "import") {
      setShowImportScreen(true);
      setShowProductDrawer(false);
      setShowSupplierDrawer(false);
      setShowCustomerDrawer(false);
      setShowCreateModal(false);
      return;
    }

    const navMap = {
      inventory: "inventory",
      invoice: "invoice",
      supplier: "suppliers",
      customer: "customers",
      report: "reports",
    };

    const nextKey = navMap[selectedCreateKey];
    if (nextKey) {
      setActiveKey(nextKey);
    }
    resetProductFlow();
    resetSupplierFlow();
    setShowCreateModal(false);
    setSelectedCreateKey(null);
  };

  const handleCreateProduct = async (form) => {
    setProductFormError("");
    setProductSaving(true);

    const payload = buildProductPayload(form);

    try {
      const created = await createProduct(payload);
      if (!created) {
        throw new Error("No product returned from the API.");
      }
      const nextRow = mapProductToRow(created);
      setInventoryState((prev) => ({
        ...prev,
        rows: [nextRow, ...prev.rows],
        source: "api",
        error: "",
        loading: false,
      }));
      setActiveKey("inventory");
      handleCloseCreate();
      setToast({ message: "Product created successfully.", tone: "success" });
    } catch (err) {
      console.error("Unable to create product", err);
      setProductFormError(err?.message || "Unable to create product. Please try again.");
    } finally {
      setProductSaving(false);
    }
  };

  const handleCreateSupplier = async (form) => {
    setSupplierFormError("");
    setSupplierSaving(true);
    let success = false;

    const payload = buildSupplierPayload(form);
    const isImportContext = supplierDrawerContext === "import";

    try {
      const created = await createSupplier(payload);
      if (!created) {
        throw new Error("No supplier returned from the API.");
      }

      const nextRow = mapSupplierToRow(created);
      setSuppliersState((prev) => ({
        ...prev,
        rows: [nextRow, ...prev.rows],
        source: "api",
        error: "",
        loading: false,
      }));
      setToast({ message: "Supplier created successfully.", tone: "success" });
      if (!isImportContext) {
        setActiveKey("suppliers");
      }
      handleCloseSupplierDrawer();
      success = true;
    } catch (err) {
      console.error("Unable to create supplier", err);
      setSupplierFormError(err?.message || "Unable to create supplier. Please try again.");
    } finally {
      setSupplierSaving(false);
    }

    return success;
  };

  const handleCreateCustomer = async (form) => {
    setCustomerFormError("");
    setCustomerSaving(true);

    const payload = buildCustomerPayload(form);

    try {
      const created = await createCustomer(payload);
      if (!created) {
        throw new Error("No customer returned from the API.");
      }

      const nextRow = mapCustomerToRow(created);
      setCustomersState((prev) => ({
        ...prev,
        rows: [nextRow, ...prev.rows],
        source: "api",
        error: "",
        loading: false,
      }));
      setActiveKey("customers");
      handleCloseCreate();
      setToast({ message: "Customer created successfully.", tone: "success" });
    } catch (err) {
      console.error("Unable to create customer", err);
      setCustomerFormError(err?.message || "Unable to create customer. Please try again.");
    } finally {
      setCustomerSaving(false);
    }
  };

  const handleCreateCustomerInline = async (form) => {
    const payload = buildCustomerPayload(form);

    try {
      const created = await createCustomer(payload);
      if (!created) {
        throw new Error("No customer returned from the API.");
      }

      const nextRow = mapCustomerToRow(created);
      setCustomersState((prev) => ({
        ...prev,
        rows: [nextRow, ...prev.rows],
        source: "api",
        error: "",
        loading: false,
      }));

      return {
        id: normalizeId(created?.id || created?._id || nextRow.id),
        name: created?.name || form?.name || "",
        email: created?.contact?.email,
        phone: created?.contact?.phone,
        address: created?.address || form?.address,
        notes: created?.notes,
      };
    } catch (err) {
      console.error("Unable to create customer inline", err);
      throw err;
    }
  };

  const handleCreateInvoice = async (payload) => {
    setInvoiceFormError("");
    setInvoiceSaving(true);

    try {
      const targetId = editingInvoiceId || lastSavedInvoice?._id || lastSavedInvoice?.id;
      const saved = targetId ? await updateInvoice(targetId, payload) : await createInvoice(payload);
      if (!saved) {
        throw new Error("No invoice returned from the API.");
      }

      const nextRow = mapInvoiceToRow(saved);
      setInvoicesState((prev) => {
        const exists = prev.rows.some((row) => row.id === nextRow.id);
        const rows = exists
          ? prev.rows.map((row) => (row.id === nextRow.id ? nextRow : row))
          : [nextRow, ...prev.rows];
        return {
          ...prev,
          rows,
          source: "api",
          error: "",
          loading: false,
        };
      });
      setLastSavedInvoice(saved);
      setEditingInvoiceId(nextRow.id);
      setToast({ message: targetId ? "Invoice updated." : "Invoice saved successfully.", tone: "success" });
      return saved;
    } catch (err) {
      console.error("Unable to create invoice", err);
      setInvoiceFormError(err?.message || "Unable to save invoice. Please try again.");
      throw err;
    } finally {
      setInvoiceSaving(false);
    }
  };

  const handleLogInvoiceEvent = async (invoiceId, payload = {}) => {
    const cleanId = normalizeId(invoiceId);
    if (!cleanId) return null;

    try {
      const updated = await logInvoiceEvent(cleanId, payload);
      if (updated) {
        const nextRow = mapInvoiceToRow(updated);
        setInvoicesState((prev) => {
          const exists = prev.rows.some((row) => row.id === nextRow.id);
          const rows = exists ? prev.rows.map((row) => (row.id === nextRow.id ? nextRow : row)) : [nextRow, ...prev.rows];
          return {
            ...prev,
            rows,
            source: "api",
            error: "",
            loading: false,
          };
        });
        setLastSavedInvoice(updated);
      }
      return updated;
    } catch (err) {
      console.error("Unable to log invoice event", err);
      return null;
    }
  };

  const handleRecordInvoicePayment = async (invoiceId, payload = {}) => {
    const cleanId = normalizeId(invoiceId);
    if (!cleanId) return null;
    try {
      const updated = await recordInvoicePayment(cleanId, payload);
      if (updated) {
        const nextRow = mapInvoiceToRow(updated);
        setInvoicesState((prev) => {
          const exists = prev.rows.some((row) => row.id === nextRow.id);
          const rows = exists ? prev.rows.map((row) => (row.id === nextRow.id ? nextRow : row)) : [nextRow, ...prev.rows];
          return {
            ...prev,
            rows,
            source: "api",
            error: "",
            loading: false,
          };
        });
        setLastSavedInvoice(updated);
      }
      return updated;
    } catch (err) {
      console.error("Unable to record invoice payment", err);
      setInvoiceFormError(err?.message || "Unable to record payment.");
      return null;
    }
  };

  const handleUpdateSupplier = async (form, existingSupplier) => {
    setSupplierFormError("");
    setSupplierSaving(true);
    let success = false;

    const targetSupplier = existingSupplier || selectedSupplier;
    const id = normalizeId(targetSupplier?.id || targetSupplier?._id || form?.id);
    if (!id) {
      setSupplierFormError("Unable to update: missing supplier id.");
      setSupplierSaving(false);
      return false;
    }

    const payload = buildSupplierPayload(form);

    try {
      const updated = await updateSupplier(id, payload);
      const updatedSupplier = updated?.value || updated;
      const mergedSupplier =
        (updatedSupplier && typeof updatedSupplier === "object"
          ? updatedSupplier
          : {
              ...targetSupplier,
              ...payload,
            }) || {};
      const normalizedSupplier = {
        ...mergedSupplier,
        id: normalizeId(mergedSupplier.id || mergedSupplier._id || id),
        _id: mergedSupplier._id || mergedSupplier.id || id,
      };
      const nextRow = mapSupplierToRow(normalizedSupplier);
      setSuppliersState((prev) => {
        const nextRows = prev.rows.map((row) => (row.id === nextRow.id ? nextRow : row));
        return {
          ...prev,
          rows: nextRows,
          source: "api",
          error: "",
          loading: false,
        };
      });
      setSelectedSupplier(normalizedSupplier);
      setSupplierDrawerMode("edit");
      setToast({ message: "Supplier updated successfully.", tone: "success" });
      setSupplierLinkError("");
      handleCloseSupplierDrawer();
      success = true;
    } catch (err) {
      console.error("Unable to update supplier", err);
      setSupplierFormError(err?.message || "Unable to update supplier. Please try again.");
    } finally {
      setSupplierSaving(false);
    }

    return success;
  };

  const handleLinkProductsToSupplier = async (supplierId, productIds = []) => {
    const cleanSupplierId = normalizeId(supplierId);
    const cleanProductIds = (productIds || []).map((pid) => normalizeId(pid)).filter(Boolean);
    if (!cleanSupplierId || !cleanProductIds.length) return;

    setSupplierLinkError("");
    setSupplierLinking(true);

    try {
      const updates = await Promise.all(
        cleanProductIds.map(async (pid) => {
          const updated = await updateProduct(pid, { supplierId: cleanSupplierId });
          const updatedProduct = updated?.value || updated;
          return {
            ...(updatedProduct || {}),
            id: normalizeId(updatedProduct?.id || updatedProduct?._id || pid),
            _id: updatedProduct?._id || updatedProduct?.id || pid,
            supplierId: cleanSupplierId,
          };
        })
      );

      setInventoryState((prev) => {
        const nextRows = prev.rows.map((row) => {
          const match = updates.find((p) => row.id === normalizeId(p.id || p._id));
          if (!match) return row;
          const mergedProduct = { ...(row.product || {}), ...match };
          return mapProductToRow(mergedProduct);
        });

        updates.forEach((prod) => {
          const rowId = normalizeId(prod.id || prod._id);
          if (!nextRows.some((row) => row.id === rowId)) {
            nextRows.unshift(mapProductToRow(prod));
          }
        });

        return {
          ...prev,
          rows: nextRows,
          source: "api",
          error: "",
          loading: false,
        };
      });

      if (selectedSupplier && normalizeId(selectedSupplier.id || selectedSupplier._id) === cleanSupplierId) {
        setSelectedSupplier((prev) => ({ ...prev, id: cleanSupplierId }));
      }

      setSupplierLinkError("");
      setToast({ message: "Products linked to supplier.", tone: "success" });
    } catch (err) {
      console.error("Unable to link products to supplier", err);
      setSupplierLinkError(err?.message || "Unable to link products to supplier. Please try again.");
    } finally {
      setSupplierLinking(false);
    }
  };

  const handleDelinkProductFromSupplier = async (productIds) => {
    const ids = Array.isArray(productIds) ? productIds : [productIds];
    const cleanIds = ids.map((pid) => normalizeId(pid)).filter(Boolean);
    if (!cleanIds.length) return;

    setSupplierLinkError("");
    setSupplierLinking(true);

    try {
      const updates = await Promise.all(
        cleanIds.map(async (pid) => {
          const updated = await updateProduct(pid, { supplierId: "" });
          const updatedProduct = updated?.value || updated || {};
          return {
            ...updatedProduct,
            id: normalizeId(updatedProduct.id || updatedProduct._id || pid),
            _id: updatedProduct._id || updatedProduct.id || pid,
            supplierId: "",
          };
        })
      );

      setInventoryState((prev) => {
        const nextRows = prev.rows.map((row) => {
          const match = updates.find((p) => row.id === normalizeId(p.id || p._id));
          if (!match) return row;
          const mergedProduct = { ...(row.product || {}), ...match };
          return mapProductToRow(mergedProduct);
        });

        updates.forEach((prod) => {
          const rowId = normalizeId(prod.id || prod._id);
          if (!nextRows.some((row) => row.id === rowId)) {
            nextRows.unshift(mapProductToRow(prod));
          }
        });

        return {
          ...prev,
          rows: nextRows,
          source: "api",
          error: "",
          loading: false,
        };
      });

      setToast({
        message: updates.length > 1 ? "Products unlinked from supplier." : "Product unlinked from supplier.",
        tone: "success",
      });
    } catch (err) {
      console.error("Unable to unlink product from supplier", err);
      setSupplierLinkError(err?.message || "Unable to unlink product from supplier. Please try again.");
    } finally {
      setSupplierLinking(false);
    }
  };

  const handleUpdateCustomer = async (form, existingCustomer) => {
    setCustomerFormError("");
    setCustomerSaving(true);

    const targetCustomer = existingCustomer || selectedCustomer;
    const id = normalizeId(targetCustomer?.id || targetCustomer?._id || form?.id);
    if (!id) {
      setCustomerFormError("Unable to update: missing customer id.");
      setCustomerSaving(false);
      return;
    }

    const payload = buildCustomerPayload(form);

    try {
      const updated = await updateCustomer(id, payload);
      const updatedCustomer = updated?.value || updated;
      const mergedCustomer =
        (updatedCustomer && typeof updatedCustomer === "object"
          ? updatedCustomer
          : {
              ...targetCustomer,
              ...payload,
            }) || {};
      const normalizedCustomer = {
        ...mergedCustomer,
        id: normalizeId(mergedCustomer.id || mergedCustomer._id || id),
        _id: mergedCustomer._id || mergedCustomer.id || id,
      };
      const nextRow = mapCustomerToRow(normalizedCustomer);
      setCustomersState((prev) => {
        const nextRows = prev.rows.map((row) => (row.id === nextRow.id ? nextRow : row));
        return {
          ...prev,
          rows: nextRows,
          source: "api",
          error: "",
          loading: false,
        };
      });
      setSelectedCustomer(normalizedCustomer);
      setCustomerDrawerMode("edit");
      handleCloseCreate();
      setToast({ message: "Customer updated successfully.", tone: "success" });
    } catch (err) {
      console.error("Unable to update customer", err);
      setCustomerFormError(err?.message || "Unable to update customer. Please try again.");
    } finally {
      setCustomerSaving(false);
    }
  };

  const handleUpdateProduct = async (form, existingProduct) => {
    setProductFormError("");
    setProductSaving(true);

    const targetProduct = existingProduct || selectedProduct;
    const id = normalizeId(targetProduct?.id || targetProduct?._id || form?.id);
    if (!id) {
      setProductFormError("Unable to update: missing product id.");
      setProductSaving(false);
      return;
    }

    const payload = buildProductPayload(form);

    try {
      const updated = await updateProduct(id, payload);
      const updatedProduct = updated?.value || updated;
      const mergedProduct =
        (updatedProduct && typeof updatedProduct === "object"
          ? updatedProduct
          : {
              ...targetProduct,
              ...payload,
            }) || {};
      const normalizedProduct = {
        ...mergedProduct,
        id: normalizeId(mergedProduct.id || mergedProduct._id || id),
        _id: mergedProduct._id || mergedProduct.id || id,
      };
      const nextRow = mapProductToRow(normalizedProduct);
      setInventoryState((prev) => ({
        ...prev,
        rows: prev.rows.map((row) => (row.id === nextRow.id ? nextRow : row)),
        source: "api",
        error: "",
        loading: false,
      }));
      setSelectedProduct(normalizedProduct);
      setDrawerMode("edit");
      setToast({ message: "Product updated successfully.", tone: "success" });
      handleCloseCreate();
    } catch (err) {
      console.error("Unable to update product", err);
      setProductFormError(err?.message || "Unable to update product. Please try again.");
    } finally {
      setProductSaving(false);
    }
  };

  const handleActionButton = () => {
    if (activeKey === "invoice") {
      resetInvoiceFlow();
      setInvoiceMode((prev) => (prev === "list" ? "create" : "list"));
      return;
    }

    if (activeKey === "inventory") {
      openProductDrawer({ withCreateModal: false });
      return;
    }

    if (activeKey === "suppliers") {
      openSupplierDrawer({ withCreateModal: false });
      return;
    }

    if (activeKey === "customers") {
      openCustomerDrawer({ withCreateModal: false });
      return;
    }

    if (activeKey === "settings") {
      if (!isAdminUser) return;
      setSettingsDrawerSignal((prev) => prev + 1);
      return;
    }

    if (activeKey === "employees") {
      if (!isAdminUser) return;
      setEmployeesDrawerSignal((prev) => prev + 1);
      return;
    }

    const createKeyMap = {
      invoice: "invoice",
      suppliers: "supplier",
      customers: "customer",
      users: "customer",
      reports: "report",
    };

    resetProductFlow();
    resetSupplierFlow();
    resetCustomerFlow();
    setShowCreateModal(true);

    const createKey = createKeyMap[activeKey];
    if (createKey) {
      setSelectedCreateKey(createKey);
    } else {
      setSelectedCreateKey(null);
    }
    setShowProductDrawer(false);
    setShowSupplierDrawer(false);
    setShowCustomerDrawer(false);
    setShowImportScreen(false);
  };

  const formatPrice = useCallback((value) => {
    if (value === null || value === undefined) return "—";
    const numeric = typeof value === "string" ? Number(value) : value;
    if (Number.isNaN(numeric)) return `${value}`;
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  }, []);

  const formatExpiry = useCallback((value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }, []);

  const formatMoney = useCallback((value, currency = "KWD") => {
    if (value === null || value === undefined || value === "") return `${currency} 0.000`;
    const numeric = typeof value === "string" ? Number(value) : value;
    if (Number.isNaN(numeric)) return `${currency} ${value}`;
    return `${currency} ${numeric.toLocaleString(undefined, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })}`;
  }, []);

  const formatDate = useCallback((value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }, []);

  const normalizeId = useCallback((value) => {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    if (value?.$oid) return value.$oid;
    return value.toString?.();
  }, []);

  const openBarcodeModal = useCallback(
    ({ preselectIds } = {}) => {
      const list =
        preselectIds instanceof Set
          ? Array.from(preselectIds)
          : Array.isArray(preselectIds)
          ? preselectIds
          : preselectIds
          ? [preselectIds]
          : [];
      const normalized = list
        .map((id) => normalizeId(id) || (typeof id === "string" ? id.trim() : ""))
        .filter(Boolean);
      setBarcodeSelection(normalized);
      setShowBarcodeModal(true);
    },
    [normalizeId]
  );

  const closeBarcodeModal = useCallback(() => {
    setShowBarcodeModal(false);
    setBarcodeSelection([]);
  }, []);

  const mapProductToRow = useCallback(
    (product) => {
      const quantityRaw = product?.quantityOnHand ?? product?.quantity ?? 0;
      const reorderLevelRaw = product?.reorderLevel ?? 0;
      const quantityNumeric = Number(quantityRaw);
      const reorderLevelNumeric = Number(reorderLevelRaw);
      const quantity = Number.isNaN(quantityNumeric) ? 0 : quantityNumeric;
      const reorderLevel = Number.isNaN(reorderLevelNumeric) ? 0 : reorderLevelNumeric;
      const status = (product?.status || "").toLowerCase();
      const archived =
        Boolean(product?.archivedAt) ||
        product?.lifecycleStatus === "inactive" ||
        status === "inactive";

      const availability = (() => {
        if (archived) return "inactive";
        if (status.includes("inactive")) return "inactive";
        if (quantity <= 0) return "out of stock";
        if (status === "out" || status.includes("out of stock")) return "out of stock";
        if (status === "low" || status.includes("low")) return "low";
        if (reorderLevel > 0 && quantity <= reorderLevel) return "low";
        return "available";
      })();

      const statusConfig = (() => {
        if (availability === "inactive") return { label: "inactive", tone: "warn" };
        if (availability === "out of stock") return { label: "out of stock", tone: "danger" };
        if (availability === "low") return { label: "low", tone: "danger" };
        return { label: "in stock", tone: "success" };
      })();

      const refCode = product?.refCode ?? product?.sku ?? "—";
      const displayName =
        product?.name ||
        product?.title ||
        product?.productName ||
        product?.item?.title ||
        product?.description ||
        refCode ||
        "Untitled product";
      const imageKeyCandidates = normalizeAssetKeys(product?.imageKeys);
      const primaryImageKey = imageKeyCandidates.find(Boolean) || "";
      const legacyImageValue = typeof product?.imageUrl === "string" ? product.imageUrl.trim() : "";
      const legacyDirectUrl = resolveAssetUrl(legacyImageValue);
      const legacyKey = legacyDirectUrl ? "" : legacyImageValue;
      const imageKey = primaryImageKey || legacyKey || "";
      const resolvedImageUrl = legacyDirectUrl || "";

      return {
        id: normalizeId(product?.id || product?._id),
        image: imageKey || resolvedImageUrl ? { type: "image", key: imageKey, url: resolvedImageUrl, alt: displayName } : "",
        name: displayName,
        refCode,
        quantity: `${quantity}`,
        price: formatPrice(product?.defaultPrice),
        status: {
          type: "status",
          label: statusConfig.label,
          tone: statusConfig.tone,
        },
        expiry: formatExpiry(product?.expiryDate),
        availability,
        product: {
          ...product,
          name: displayName,
          id: normalizeId(product?.id || product?._id),
          refCode,
          lifecycleStatus: product?.lifecycleStatus || (archived ? "inactive" : "active"),
        },
        archived,
      };
    },
    [formatExpiry, formatPrice, normalizeId]
  );

  useEffect(() => {
    if (!inventoryState.rows?.length) return;
    const urlEntries = Object.entries(signedInventoryUrls || {});
    if (!urlEntries.length) return;

    setInventoryState((prev) => {
      let didUpdate = false;
      const nextRows = prev.rows.map((row) => {
        const image = row?.image;
        if (!image || image.type !== "image") return row;
        if (image.url) return row;
        const key = image.key;
        const resolved = resolveAssetUrl(key, signedInventoryUrls);
        if (!resolved) return row;
        didUpdate = true;
        return { ...row, image: { ...image, url: resolved } };
      });
      if (!didUpdate) return prev;
      return { ...prev, rows: nextRows };
    });
  }, [inventoryState.rows, signedInventoryUrls]);

  const mapSupplierToRow = useCallback((supplier) => {
    const contact = supplier?.contact || {};
    const title = supplier?.name || "Untitled supplier";
    const baseSubtitle = supplier?.address || contact.email || supplier?.notes || "—";
    const products = supplier?.productCount ?? supplier?.productsCount;
    const avatar =
      title
        .split(" ")
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "🏥";
    const archived = Boolean(supplier?.archivedAt);
    const subtitle = archived ? `${baseSubtitle} (hidden)` : baseSubtitle;

    return {
      id: normalizeId(supplier?.id || supplier?._id),
      item: { type: "lead", title, subtitle, avatar },
      phone: contact.phone || "—",
      products: products !== undefined && products !== null ? `${products}` : "—",
      supplier: {
        ...supplier,
        archivedAt: archived ? supplier.archivedAt : undefined,
      },
    };
  }, [normalizeId]);

  const mapCustomerToRow = useCallback((customer) => {
    const contact = customer?.contact || {};
    const title = customer?.name || "Untitled customer";
    const subtitle = customer?.address || contact.email || contact.phone || customer?.notes || "—";
    const avatar =
      title
        .split(" ")
        .map((part) => part[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "👥";
    return {
      id: normalizeId(customer?.id || customer?._id),
      item: { type: "lead", title, subtitle, avatar },
      phone: contact.phone || "—",
      email: contact.email || "—",
      customer: {
        ...customer,
        archivedAt: undefined,
        status: undefined,
      },
    };
  }, [normalizeId]);

  const mapInvoiceToRow = useCallback(
    (invoice) => {
      const id = normalizeId(invoice?._id || invoice?.id || invoice?.invoiceNumber);
      const statusRaw = (invoice?.status || "draft").toString().toLowerCase();
      const statusValue = ["sent", "issued"].includes(statusRaw)
        ? "unpaid"
        : ["draft", "unpaid", "partial", "paid", "overdue", "cancelled", "void"].includes(statusRaw)
        ? statusRaw
        : "draft";
      const statusLabelMap = {
        draft: "Draft",
        unpaid: "Unpaid",
        partial: "Partially Paid",
        paid: "Paid",
        overdue: "Overdue",
        cancelled: "Cancelled",
        void: "Void",
      };
      const statusToneMap = {
        paid: "success",
        unpaid: "info",
        draft: "warn",
        partial: "warn",
        overdue: "danger",
        cancelled: "danger",
        void: "warn",
      };
      const statusLabel = statusLabelMap[statusValue] || "Draft";
      const statusTone = statusToneMap[statusValue] || "info";

      const itemsCount = Array.isArray(invoice?.items)
        ? invoice.items.length
        : Number(invoice?.items || invoice?.itemsCount || 0) || 0;
      const currency = invoice?.currency || "KWD";
      const buildDateCell = (value) => {
        if (!value) {
          return { display: "—", label: "—", sortValue: null };
        }
        const date = new Date(value);
        const valid = !Number.isNaN(date.getTime());
        const display = valid ? formatDate(date) : "—";
        return { display, label: display, sortValue: valid ? date.getTime() : null };
      };
      const createdDate = invoice?.issueDate ?? invoice?.createdAt ?? invoice?.created_at;
      const dueDate = invoice?.dueDate ?? invoice?.expectedPaymentDate ?? invoice?.due_date;

      return {
        id: id || invoice?.invoiceNumber || invoice?.invoice_no || invoice?.invoiceNo || invoice?.reference || Math.random(),
        item: {
          type: "lead",
          title: invoice?.customerName || "Unassigned customer",
          subtitle: `${invoice?.invoiceNumber || id || "Draft"} · v${invoice?.revision || 1}`,
        },
        date: buildDateCell(createdDate),
        dueDate: buildDateCell(dueDate),
        items: `${itemsCount}`,
        total: formatMoney(invoice?.total ?? invoice?.amount ?? 0, currency),
        status: { type: "status", label: statusLabel, tone: statusTone },
        invoice: {
          ...invoice,
          id,
          _id: invoice?._id || id,
          currency,
          status: statusValue || "draft",
          revision: invoice?.revision || 1,
        },
      };
    },
    [formatDate, formatMoney, normalizeId]
  );

  const loadInventory = useCallback(async (options = {}) => {
    const silent = options.silent;
    setInventoryState((prev) => ({ ...prev, loading: silent ? prev.loading : true, error: "" }));
    try {
      const products = await fetchProducts({ includeArchived: true });
      if (!Array.isArray(products)) {
        throw new Error("Unexpected response while loading inventory.");
      }
      const rows = products.map(mapProductToRow);

      setInventoryState({
        rows,
        loading: false,
        error: "",
        source: "api",
      });
      return rows;
    } catch (err) {
      console.error("Unable to load inventory from API", err);
      setInventoryState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || "Unable to reach the API",
        source: "error",
      }));
      return null;
    }
  }, [mapProductToRow]);

  const loadSuppliers = useCallback(async () => {
    setSuppliersState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const suppliers = await fetchSuppliers({ includeArchived: true });
      if (!Array.isArray(suppliers)) {
        throw new Error("Unexpected response while loading suppliers.");
      }
      const rows = suppliers.map(mapSupplierToRow);
      setSuppliersState({
        rows,
        loading: false,
        error: "",
        source: "api",
      });
    } catch (err) {
      console.error("Unable to load suppliers from API", err);
      setSuppliersState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || "Unable to reach the API",
        source: "error",
      }));
    }
  }, [mapSupplierToRow]);

  const handleToggleSupplierStatus = useCallback(
    async (supplierData, options = {}) => {
      const candidate =
        supplierData && typeof supplierData === "object" && !Array.isArray(supplierData)
          ? supplierData
          : selectedSupplier;
      const fallbackId = typeof supplierData === "string" ? supplierData : undefined;
      const supplierId = normalizeId(candidate?.id || candidate?._id || fallbackId);
      if (!supplierId) {
        setSupplierFormError("Unable to update supplier status: missing id.");
        return;
      }
      const archivedOverride = typeof options.archived === "boolean" ? options.archived : undefined;
      const isArchived = archivedOverride !== undefined ? archivedOverride : Boolean(candidate?.archivedAt);
      setSupplierStatusChanging(true);
      setSupplierFormError("");
      try {
        if (isArchived) {
          await restoreSupplier(supplierId);
        } else {
          await archiveSupplier(supplierId);
        }
        await loadSuppliers();
        setSelectedSupplier((prev) => {
          if (!prev) return prev;
          const prevId = normalizeId(prev.id || prev._id);
          if (prevId !== supplierId) return prev;
          return isArchived ? { ...prev, archivedAt: undefined } : { ...prev, archivedAt: new Date().toISOString() };
        });
        setToast({
          message: isArchived ? "Supplier restored successfully." : "Supplier archived successfully.",
          tone: "success",
        });
      } catch (err) {
        console.error("Unable to toggle supplier status", err);
        setSupplierFormError(err?.message || "Unable to update supplier status. Please try again.");
      } finally {
        setSupplierStatusChanging(false);
      }
    },
    [loadSuppliers, normalizeId, selectedSupplier, setToast]
  );

  const loadCustomers = useCallback(async () => {
    setCustomersState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const customers = await fetchCustomers({ includeArchived: true });
      if (!Array.isArray(customers)) {
        throw new Error("Unexpected response while loading customers.");
      }
      const rows = customers.map(mapCustomerToRow);
      setCustomersState({
        rows,
        loading: false,
        error: "",
        source: "api",
      });
    } catch (err) {
      console.error("Unable to load customers from API", err);
      setCustomersState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || "Unable to reach the API",
        source: "error",
      }));
    }
  }, [mapCustomerToRow]);

  const loadInvoices = useCallback(async () => {
    setInvoicesState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const response = await fetchInvoices({ limit: 200 });
      const items = Array.isArray(response) ? response : response?.items || [];
      const rows = items.map(mapInvoiceToRow);
      setInvoicesState({
        rows,
        loading: false,
        error: "",
        source: "api",
      });
    } catch (err) {
      console.error("Unable to load invoices from API", err);
      setInvoicesState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || "Unable to reach the API",
        source: "error",
      }));
    }
  }, [mapInvoiceToRow]);

  const updateInventoryRows = useCallback((updater) => {
    setInventoryState((prev) => {
      const nextRows = typeof updater === "function" ? updater(prev.rows) : updater || [];
      return { ...prev, rows: nextRows };
    });
  }, []);

  useEffect(() => {
    if (session) {
      loadInventory();
    }
  }, [session, loadInventory]);

  useEffect(() => {
    if (session) {
      loadSuppliers();
    }
  }, [session, loadSuppliers]);

  useEffect(() => {
    if (session) {
      loadCustomers();
    }
  }, [session, loadCustomers]);

  useEffect(() => {
    if (session) {
      loadInvoices();
    }
  }, [session, loadInvoices]);

  const handleProductsImported = async ({ inserted = 0 } = {}) => {
    await loadInventory();
    setActiveKey("inventory");
    setShowImportScreen(false);
    setShowCreateModal(false);
    setSelectedCreateKey(null);
    setToast({
      message: inserted ? `Imported ${inserted} product${inserted === 1 ? "" : "s"}.` : "Products imported successfully.",
      tone: "success",
    });
  };

  const handleOpenInventoryBarcodeModal = useCallback(
    ({ preselectIds } = {}) => openBarcodeModal({ preselectIds }),
    [openBarcodeModal]
  );

  const handleOpenProductBarcodeModal = useCallback(
    (payload = {}) => {
      if (payload?.preselectIds) {
        openBarcodeModal({ preselectIds: payload.preselectIds });
        return;
      }
      const preselectIds = [
        normalizeId(payload?.id || payload?._id),
        payload?.refCode,
        payload?.sku,
        payload?.barcode,
      ].filter(Boolean);
      openBarcodeModal({ preselectIds });
    },
    [normalizeId, openBarcodeModal]
  );

  const handleInventoryRowClick = useCallback((row) => {
    if (!row) return;
    setProductSaving(false);
    setProductFormError("");
    setToast((prev) => ({ ...prev, message: "" }));
    const hasPersistedId = Boolean(row?.id || row?.product?._id || row?.product?.id);
    setDrawerMode(hasPersistedId ? "edit" : "view");
    setSelectedProduct(
      row.product
        ? { ...row.product, id: normalizeId(row.product.id || row.product._id) }
        : { ...row, id: normalizeId(row.id || row._id) }
    );
    setSelectedCreateKey("inventory");
    setShowCreateModal(false);
    setShowProductDrawer(true);
  }, [normalizeId]);

  const handleSupplierRowClick = useCallback(
    (row) => {
      if (!row) return;
      setSupplierSaving(false);
      setSupplierFormError("");
      setToast((prev) => ({ ...prev, message: "" }));
      const hasPersistedId = Boolean(row?.id || row?.supplier?._id || row?.supplier?.id);
      setSupplierDrawerMode(hasPersistedId ? "edit" : "create");
      setSelectedSupplier(
        row.supplier
          ? { ...row.supplier, id: normalizeId(row.supplier.id || row.supplier._id) }
          : { ...row, id: normalizeId(row.id || row._id) }
      );
      setSelectedCreateKey("supplier");
      setShowCreateModal(false);
      setShowSupplierDrawer(true);
      setShowProductDrawer(false);
    },
    [normalizeId]
  );

  const handleCustomerRowClick = useCallback(
    (row) => {
      if (!row) return;
      setCustomerSaving(false);
      setCustomerFormError("");
      setToast((prev) => ({ ...prev, message: "" }));
      const hasPersistedId = Boolean(row?.id || row?.customer?._id || row?.customer?.id);
      setCustomerDrawerMode(hasPersistedId ? "edit" : "create");
      setSelectedCustomer(
        row.customer
          ? { ...row.customer, id: normalizeId(row.customer.id || row.customer._id) }
          : { ...row, id: normalizeId(row.id || row._id) }
      );
      setSelectedCreateKey("customer");
      setShowCreateModal(false);
      setShowCustomerDrawer(true);
      setShowSupplierDrawer(false);
      setShowProductDrawer(false);
    },
    [normalizeId]
  );

  const handleInvoiceRowClick = useCallback(
    async (row) => {
      const id = normalizeId(row?.invoice?.id || row?.invoice?._id || row?.id || row?._id);
      if (!id) return;
      setInvoiceFormError("");
      setInvoiceSaving(false);
      setLastSavedInvoice(null);
      setInvoiceBuilderLoading(true);
      setActiveKey("invoice");
      setInvoiceMode("edit");
      setEditingInvoiceId(id);
      try {
        const invoice = await fetchInvoice(id);
        setLastSavedInvoice(invoice);
      } catch (err) {
        console.error("Unable to load invoice", err);
        setInvoiceFormError(err?.message || "Unable to load invoice.");
      } finally {
        setInvoiceBuilderLoading(false);
      }
    },
    [normalizeId]
  );

  const inventoryConfig = useMemo(
    () => ({
      ...SCREEN_CONFIG.inventory,
      rows: inventoryState.rows,
    }),
    [inventoryState.rows]
  );
  const inventoryStatus = useMemo(
    () => ({
      loading: inventoryState.loading,
      error: inventoryState.error,
      source: inventoryState.source,
      onReload: loadInventory,
    }),
    [inventoryState, loadInventory]
  );
  const inventoryProducts = useMemo(
    () =>
      inventoryState.rows
        .map((row) => {
          const product = row.product || {};
          const name =
            row.name ||
            product.name ||
            product?.item?.title ||
            product?.description ||
            product.refCode ||
            product.sku ||
            row?.refCode ||
            "Untitled product";
          const refCode = product.refCode || product.sku || row?.refCode;
          return { ...product, name, refCode };
        })
        .filter((prod) => prod && !prod.archivedAt && prod.lifecycleStatus !== "inactive" && prod.status !== "inactive"),
    [inventoryState.rows]
  );
  const supplierProductCounts = useMemo(() => {
    const counts = new Map();
    inventoryProducts.forEach((prod) => {
      const supplierId = normalizeId(prod?.supplierId || prod?.supplier?.id || prod?.supplier?._id);
      if (!supplierId) return;
      counts.set(supplierId, (counts.get(supplierId) || 0) + 1);
    });
    return counts;
  }, [inventoryProducts, normalizeId]);
  const suppliersWithProductCounts = useMemo(() => {
    if (!Array.isArray(suppliersState.rows)) return suppliersState.rows;
    const toNumber = (value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    };
    return suppliersState.rows.map((row) => {
      const supplierId = normalizeId(row?.supplier?.id || row?.supplier?._id || row?.id);
      const derivedCount = supplierId ? supplierProductCounts.get(supplierId) : undefined;
      const existingCount =
        toNumber(row?.supplier?.productCount) ??
        toNumber(row?.supplier?.productsCount) ??
        toNumber(row?.products);
      const finalCount = derivedCount ?? existingCount ?? 0;
      const supplierData = row?.supplier ? { ...row.supplier } : {};
      return {
        ...row,
        products: `${finalCount}`,
        supplier: { ...supplierData, productCount: finalCount, productsCount: finalCount },
      };
    });
  }, [normalizeId, supplierProductCounts, suppliersState.rows]);
  const invoicesStatus = useMemo(
    () => ({
      loading: invoicesState.loading,
      error: invoicesState.error,
      source: invoicesState.source,
      onReload: loadInvoices,
    }),
    [invoicesState, loadInvoices]
  );
  const supplierOptions = useMemo(() => {
    const seen = new Set();
    return suppliersState.rows
      .map((row) => {
        const supplier = row?.supplier || {};
        const id = normalizeId(supplier.id || supplier._id || row.id);
        if (!id || seen.has(id)) return null;
        seen.add(id);
        return { id, name: supplier.name || row?.item?.title || "Unnamed supplier" };
      })
      .filter(Boolean);
  }, [suppliersState.rows, normalizeId]);
  const customerOptions = useMemo(() => {
    const seen = new Set();
    return customersState.rows
      .map((row) => {
        const customer = row?.customer || {};
        const contact = customer.contact || {};
        const id = normalizeId(customer.id || customer._id || row.id);
        if (!id || seen.has(id)) return null;
        seen.add(id);
        return {
          id,
          name: customer.name || row?.item?.title || "Unnamed customer",
          email: contact.email,
          phone: contact.phone,
          address: customer.address || row?.item?.subtitle,
          notes: customer.notes,
        };
      })
      .filter(Boolean);
  }, [customersState.rows, normalizeId]);
  const suppliersConfig = useMemo(
    () => ({
      ...SCREEN_CONFIG.suppliers,
      rows: suppliersWithProductCounts,
    }),
    [suppliersWithProductCounts]
  );
  const customersConfig = useMemo(
    () => ({
      ...SCREEN_CONFIG.customers,
      rows: customersState.rows,
    }),
    [customersState.rows]
  );
  const invoiceConfig = useMemo(
    () => ({
      ...SCREEN_CONFIG.invoice,
      rows: invoicesState.rows,
      actionLabel: invoiceMode === "list" ? SCREEN_CONFIG.invoice.actionLabel : "← Back to invoices",
    }),
    [invoiceMode, invoicesState.rows]
  );
  const homeInventoryRows = useMemo(
    () => (Array.isArray(inventoryState.rows) ? inventoryState.rows : []),
    [inventoryState.rows]
  );
  const homeInsights = useMemo(() => {
    const inventoryRows = Array.isArray(homeInventoryRows) ? homeInventoryRows : [];
    const supplierRows = Array.isArray(suppliersState.rows) ? suppliersState.rows : [];
    const invoiceRows = Array.isArray(invoicesState.rows) ? invoicesState.rows : [];
    const actorPool = [];
    const addActor = (value) => {
      if (value && !actorPool.includes(value)) {
        actorPool.push(value);
      }
    };
    addActor(session?.role);
    addActor(session?.email);
    if (!actorPool.length) actorPool.push("Admin");

    const getActorName = (row, fallbackIndex = 0) => {
      const candidates = [
        row?.actor,
        row?.user,
        row?.userName,
        row?.performedBy,
        row?.createdBy,
        row?.updatedBy,
        row?.owner,
        row?.invoice?.createdBy,
        row?.invoice?.updatedBy,
        row?.product?.createdBy,
        row?.product?.updatedBy,
      ];

      for (const candidate of candidates) {
        if (!candidate) continue;
        if (typeof candidate === "string" && candidate.trim()) return candidate;
        if (typeof candidate?.name === "string" && candidate.name) return candidate.name;
        if (typeof candidate?.email === "string" && candidate.email) return candidate.email;
      }

      return actorPool[fallbackIndex % actorPool.length] || "Admin";
    };

    const normalizeNumber = (value) => {
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
      }
      if (typeof value === "string") {
        const sanitized = Number(value.replace(/,/g, ""));
        return Number.isFinite(sanitized) ? sanitized : 0;
      }
      return 0;
    };

    const getProductQuantity = (row) => {
      const product = row?.product || {};
      const raw = product.quantityOnHand ?? product.quantity ?? row?.quantity ?? 0;
      return normalizeNumber(raw);
    };

    const getProductPrice = (row) => {
      const product = row?.product || {};
      const raw = product.defaultPrice ?? product.cost ?? row?.price ?? 0;
      return normalizeNumber(raw);
    };

    const getExpiryDate = (row) => {
      const raw = row?.product?.expiryDate || row?.expiryDate || row?.expiry;
      if (!raw) return null;
      const date = new Date(raw);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const getInventoryRowId = (row) =>
      normalizeId(row?.product?.id || row?.product?._id || row?.id || row?._id) ||
      row?.refCode ||
      row?.sku ||
      row?.name;

    const sumInvoiceAmount = (rows) =>
      rows.reduce((sum, row) => {
        const raw = row?.invoice?.total ?? row?.invoice?.amount ?? 0;
        return sum + normalizeNumber(raw);
      }, 0);
    const sumInvoiceBalance = (rows) =>
      rows.reduce((sum, row) => {
        const raw = row?.invoice?.balance ?? row?.invoice?.total ?? row?.invoice?.amount ?? 0;
        return sum + normalizeNumber(raw);
      }, 0);

    const EXPIRY_WINDOW_DAYS = 30;
    const EXPIRY_WINDOW = 1000 * 60 * 60 * 24 * EXPIRY_WINDOW_DAYS;
    const now = Date.now();
    const numberFormatter = new Intl.NumberFormat();
    const getLogTimestamp = (value, fallbackMinutes = 0) => {
      if (value) {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) return date;
      }
      return new Date(now - fallbackMinutes * 60 * 1000);
    };
    const formatRelativeTime = (date) => {
      if (!date) return "Just now";
      const diff = now - date.getTime();
      if (diff < 2 * 60 * 1000) return "Just now";
      if (diff < 60 * 60 * 1000) {
        return `${Math.max(1, Math.round(diff / (60 * 1000)))}m ago`;
      }
      if (diff < 24 * 60 * 60 * 1000) {
        return `${Math.max(1, Math.round(diff / (60 * 60 * 1000)))}h ago`;
      }
      return formatDate(date);
    };
    const auditLogEntries = [];
    let fallbackMinutesCursor = 15;
    const addAuditLog = ({ id, message, detail, meta, timestamp }) => {
      const resolvedTimestamp = getLogTimestamp(timestamp, timestamp ? 0 : fallbackMinutesCursor);
      if (!timestamp) {
        fallbackMinutesCursor += 20;
      }
      auditLogEntries.push({
        id: id || `audit-${auditLogEntries.length + 1}`,
        message,
        detail,
        meta,
        timestampMs: resolvedTimestamp.getTime(),
      });
    };
    const activeInventoryRows = [];
    const archivedInventoryRows = [];

    inventoryRows.forEach((row) => {
      const product = row?.product || {};
      const archived =
        Boolean(product?.archivedAt) ||
        product?.lifecycleStatus === "inactive" ||
        product?.status === "inactive";
      if (archived) {
        archivedInventoryRows.push(row);
      } else {
        activeInventoryRows.push(row);
      }
    });

    let totalUnits = 0;
    let inventoryValueRaw = 0;
    const expiringSoon = [];
    const lowStock = [];
    const lowRows = [];
    const outRows = [];

    activeInventoryRows.forEach((row) => {
      const quantity = getProductQuantity(row);
      totalUnits += quantity;
      inventoryValueRaw += quantity * getProductPrice(row);

      const statusLabel = (row?.status?.label || "").toLowerCase();
      if (statusLabel.includes("low")) {
        lowRows.push(row);
        lowStock.push(row);
      } else if (statusLabel.includes("out")) {
        outRows.push(row);
        lowStock.push(row);
      }

      const expiryDate = getExpiryDate(row);
      if (expiryDate) {
        const diff = expiryDate.getTime() - now;
        if (diff >= 0 && diff <= EXPIRY_WINDOW) {
          expiringSoon.push({ row, expiryDate });
        }
      }
    });

    expiringSoon.sort((a, b) => a.expiryDate - b.expiryDate);

    const getInvoiceDueDate = (row) => {
      const raw = row?.invoice?.dueDate || row?.invoice?.expectedPaymentDate;
      if (!raw) return null;
      const date = new Date(raw);
      return Number.isNaN(date.getTime()) ? null : date;
    };
    const getInvoiceBalance = (row) =>
      normalizeNumber(row?.invoice?.balance ?? row?.invoice?.total ?? row?.invoice?.amount ?? 0);
    const isInvoiceOverdue = (row) => {
      const dueDate = getInvoiceDueDate(row);
      if (!dueDate) return false;
      return getInvoiceBalance(row) > 0 && dueDate.getTime() < now;
    };

    const pipelineCounts = invoiceRows.reduce((acc, row) => {
      const rawStatus = (row?.invoice?.status || row?.status?.label || "draft").toLowerCase();
      const balance = getInvoiceBalance(row);
      const dueDate = getInvoiceDueDate(row);

      let status = rawStatus === "sent" ? "unpaid" : rawStatus || "draft";
      if (balance <= 0) status = "paid";
      else if (dueDate && dueDate.getTime() < now && !["void", "cancelled"].includes(status)) status = "overdue";
      else if (!["draft", "unpaid", "partial", "paid", "overdue", "cancelled", "void"].includes(status)) status = "draft";

      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const overdueInvoices = invoiceRows.filter(isInvoiceOverdue);
    const outstandingInvoices = invoiceRows.filter((row) => {
      const status = (row?.invoice?.status || "").toLowerCase();
      return !["paid", "cancelled", "void"].includes(status) && status !== "draft";
    });

    const invoiceCurrency =
      invoiceRows.find((row) => row?.invoice?.currency)?.invoice?.currency || "KWD";
    const outstandingAmount = sumInvoiceAmount(outstandingInvoices);
    const overdueAmount = sumInvoiceBalance(overdueInvoices);

    const invoiceUpcoming = outstandingInvoices
      .map((row) => {
        const dueValue =
          row?.invoice?.dueDate || row?.invoice?.expectedPaymentDate || row?.invoice?.issueDate;
        const dueDate = dueValue ? new Date(dueValue) : null;
        return { row, dueDate };
      })
      .sort((a, b) => {
        const timeA = a.dueDate ? a.dueDate.getTime() : Number.MAX_SAFE_INTEGER;
        const timeB = b.dueDate ? b.dueDate.getTime() : Number.MAX_SAFE_INTEGER;
        return timeA - timeB;
      })
      .slice(0, 5)
      .map(({ row, dueDate }) => ({
        id: row?.id || row?.invoice?.invoiceNumber || row?.invoice?._id || row?.item?.subtitle,
        customer: row?.item?.title || "Unnamed customer",
        invoiceNumber: row?.item?.subtitle || row?.invoice?.invoiceNumber || "—",
        dueDate: formatDate(dueDate || row?.invoice?.dueDate || row?.invoice?.issueDate),
        amount: formatMoney(
          normalizeNumber(row?.invoice?.total ?? row?.invoice?.amount ?? 0),
          row?.invoice?.currency || invoiceCurrency
        ),
        status: row?.status,
      }));

    const alerts = [];
    lowStock.slice(0, 3).forEach((row) => {
      const refCode = row?.refCode || row?.name;
      alerts.push({
        id: `low-${row?.id || refCode}`,
        title: `Low stock · ${row?.name || "Unnamed item"}`,
        description: `${getProductQuantity(row)} units on hand`,
        meta: refCode,
        tone: "warn",
      });
    });
    expiringSoon.slice(0, 3).forEach(({ row, expiryDate }) => {
      const refCode = row?.refCode || row?.name;
      alerts.push({
        id: `exp-${row?.id || refCode}`,
        title: `Expiring soon · ${row?.name || "Unknown item"}`,
        description: expiryDate ? `Expires ${formatDate(expiryDate)}` : row?.expiry,
        meta: refCode,
        tone: "danger",
      });
    });
    overdueInvoices.slice(0, 3).forEach((row) => {
      alerts.push({
        id: `inv-${row?.id || row?.invoice?._id || row?.invoice?.invoiceNumber}`,
        title: `Overdue invoice · ${row?.item?.title || "Invoice"}`,
        description: row?.item?.subtitle || "Awaiting payment",
        meta: formatMoney(
          normalizeNumber(row?.invoice?.total ?? row?.invoice?.amount ?? 0),
          row?.invoice?.currency || invoiceCurrency
        ),
        tone: "danger",
      });
    });

    const supplierSignals = supplierRows.slice(0, 4).map((row) => ({
      id: row?.id || row?.item?.title || row?.phone,
      title: row?.item?.title || "Supplier",
      subtitle: row?.item?.subtitle || row?.phone || "—",
      products: row?.products,
      status: row?.status,
    }));

    const flaggedRows = new Set();
    [...lowRows, ...outRows, ...expiringSoon.map(({ row }) => row)].forEach((row) => {
      const id = getInventoryRowId(row);
      if (id) flaggedRows.add(id);
    });
    const healthyCount = Math.max(activeInventoryRows.length - flaggedRows.size, 0);
    const formatCount = (value) => numberFormatter.format(Math.max(0, value || 0));

    const inventoryStats = [
      {
        key: "total",
        label: "Total products",
        value: formatCount(activeInventoryRows.length),
        meta: archivedInventoryRows.length ? `${formatCount(archivedInventoryRows.length)} archived` : "Active catalog",
        tone: "primary",
      },
      {
        key: "healthy",
        label: "Healthy stock",
        value: formatCount(healthyCount),
        meta: `${formatCount(activeInventoryRows.length - healthyCount)} being monitored`,
        tone: "success",
      },
      {
        key: "low-out",
        label: "Low / Out of stock",
        value: formatCount(lowRows.length + outRows.length),
        pills: [`${formatCount(lowRows.length)} low`, `${formatCount(outRows.length)} out`],
        tone: "danger",
      },
      {
        key: "expiring",
        label: "Expiring soon",
        value: formatCount(expiringSoon.length),
        meta: `Within ${EXPIRY_WINDOW_DAYS} days`,
        tone: "warn",
      },
      {
        key: "units",
        label: "Units on hand",
        value: formatCount(totalUnits),
        meta: "Across active ref codes",
        tone: "info",
      },
    ];

    const stats = [
      {
        key: "invoices",
        label: "Invoices",
        value: invoiceRows.length ? invoiceRows.length.toLocaleString() : "0",
        tone: "neutral",
        hint: `${pipelineCounts.paid || 0} paid`,
        hintTone: pipelineCounts.paid ? "success" : undefined,
      },
      {
        key: "suppliers",
        label: "Suppliers",
        value: supplierRows.length ? supplierRows.length.toLocaleString() : "0",
        tone: "success",
        hint: `${supplierSignals.length ? supplierSignals.length : supplierRows.length || 0} active`,
      },
      {
        key: "notifications",
        label: "Alerts",
        value: alerts.length ? alerts.length.toString() : "0",
        tone: alerts.length ? "danger" : "success",
        hint: alerts.length ? "Needs attention" : "All clear",
        hintTone: alerts.length ? "danger" : "success",
      },
    ];

    const lowOutRows = [...lowRows, ...outRows];
    invoiceRows.slice(0, 5).forEach((row, index) => {
      const invoice = row?.invoice || {};
      const invoiceNumber =
        invoice.invoiceNumber ||
        row?.item?.subtitle ||
        invoice.code ||
        invoice.id ||
        row?.id ||
        `INV-${index + 1}`;
      const statusLabel = (invoice.status || row?.status?.label || "").toLowerCase();
      const verb =
        statusLabel === "paid"
          ? "settled"
          : statusLabel === "partial"
          ? "partially settled"
          : statusLabel === "unpaid"
          ? "issued"
          : "updated";
      const actor = getActorName(row, index);
      const amountValue = normalizeNumber(invoice.total ?? invoice.amount ?? 0);
      addAuditLog({
        id: `audit-invoice-${invoiceNumber}-${index}`,
        message: `${actor} ${verb} invoice ${invoiceNumber}`,
        detail: row?.item?.title ? `for ${row.item.title}` : undefined,
        meta: amountValue ? formatMoney(amountValue, invoice.currency || invoiceCurrency) : "",
        timestamp:
          invoice.updatedAt ||
          invoice.issueDate ||
          invoice.createdAt ||
          row?.updatedAt ||
          row?.createdAt,
      });
    });

    lowOutRows.slice(0, 4).forEach((row, index) => {
      const refCode = row?.refCode || getInventoryRowId(row) || row?.name || `REF-${index + 1}`;
      const statusLabel = row?.status?.label || "low stock";
      const actor = getActorName(row, invoiceRows.length + index);
      addAuditLog({
        id: `audit-stock-${refCode}-${index}`,
        message: `${actor} flagged ${refCode ? `ref code ${refCode}` : "an item"} as ${statusLabel}`,
        detail: row?.name,
        meta: `${formatCount(getProductQuantity(row))} units on hand`,
        timestamp: row?.product?.updatedAt || row?.updatedAt,
      });
    });

    expiringSoon.slice(0, 3).forEach(({ row, expiryDate }, index) => {
      if (!row) return;
      const refCode = row?.refCode || getInventoryRowId(row);
      const actor = getActorName(row, invoiceRows.length + lowOutRows.length + index);
      addAuditLog({
        id: `audit-expiring-${refCode || index}`,
        message: `${actor} reviewed expiring lot ${refCode || row?.name || "ref code"}`,
        detail: expiryDate ? `Expires ${formatDate(expiryDate)}` : row?.expiry,
        meta: row?.name,
        timestamp: row?.product?.updatedAt || row?.updatedAt,
      });
    });

    const activityRows = auditLogEntries
      .sort((a, b) => b.timestampMs - a.timestampMs)
      .slice(0, 8)
      .map((entry) => ({
        id: entry.id,
        message: entry.message,
        detail: entry.detail,
        meta: entry.meta,
        timestamp: formatRelativeTime(new Date(entry.timestampMs)),
      }));

    return {
      stats,
      inventorySnapshot: {
        summary: [
          { label: "Ref codes", value: activeInventoryRows.length ? activeInventoryRows.length.toLocaleString() : "0" },
          { label: "On hand units", value: totalUnits ? totalUnits.toLocaleString() : "0" },
          { label: "Low / out", value: lowStock.length.toLocaleString() },
          { label: "Exp <30d", value: expiringSoon.length.toLocaleString() },
          { label: "Est. value", value: formatMoney(inventoryValueRaw, "KWD") },
        ],
        expiring: expiringSoon.slice(0, 4).map(({ row, expiryDate }) => ({
          id: row?.id || row?.refCode,
          name: row?.name || "Unnamed item",
          refCode: row?.refCode,
          expiry: formatDate(expiryDate || row?.expiry),
          status: row?.status,
        })),
        lowStock: lowStock.slice(0, 4).map((row) => ({
          id: row?.id || row?.refCode,
          name: row?.name || "Unnamed item",
          refCode: row?.refCode,
          quantity: getProductQuantity(row),
          status: row?.status,
        })),
      },
      invoiceSnapshot: {
        summary: [
          { label: "Draft", value: (pipelineCounts.draft || 0).toString() },
          { label: "Unpaid", value: (pipelineCounts.unpaid || 0).toString() },
          { label: "Partial", value: (pipelineCounts.partial || 0).toString() },
          { label: "Paid", value: (pipelineCounts.paid || 0).toString() },
          { label: "Overdue", value: (pipelineCounts.overdue || 0).toString() },
        ],
        upcoming: invoiceUpcoming,
        totals: {
          outstanding: formatMoney(outstandingAmount, invoiceCurrency),
          overdue: formatMoney(overdueAmount, invoiceCurrency),
        },
      },
      alerts,
      inventoryStats,
      activityRows,
    };
  }, [formatDate, formatMoney, homeInventoryRows, invoicesState.rows, normalizeId, session, suppliersState.rows]);
  const homeStats = homeInsights?.stats || [];
  const homeInventorySnapshot = homeInsights?.inventorySnapshot || { summary: [], expiring: [], lowStock: [] };
  const homeInvoiceSnapshot = homeInsights?.invoiceSnapshot || { summary: [], upcoming: [], totals: {} };
  const homeAlerts = homeInsights?.alerts || [];
  const homeInventoryStats = homeInsights?.inventoryStats || [];
  const homeActivityRows = homeInsights?.activityRows || [];
  const normalizedRole = (session?.role || "").toLowerCase();
  const isAdminUser = normalizedRole === "super admin" || normalizedRole === "super_admin" || normalizedRole === "admin";
  const canAccess = useCallback(
    (key) => {
      if (!session) return false;
      if (isAdminUser) return true;
      const eligibility = new Set(session?.eligibility || []);
      if (key === "home") return true;
      const normalized = key === "invoice" ? "invoices" : key;
      if (normalized === "employees") return true;
      return eligibility.has(normalized);
    },
    [isAdminUser, session]
  );
  const allowedNavItems = useMemo(
    () =>
      NAV_ITEMS.filter((item) => canAccess(item.key)).map((item) =>
        item.key === "employees" && !isAdminUser ? { ...item, label: "My profile" } : item
      ),
    [canAccess, isAdminUser]
  );
  useEffect(() => {
    if (!session) return;
    if (canAccess(activeKey)) return;
    const fallback = allowedNavItems[0]?.key || "home";
    setActiveKey(fallback);
  }, [activeKey, allowedNavItems, canAccess, session]);
  useEffect(() => {
    setShowImportScreen(false);
  }, [activeKey]);
  useEffect(() => {
    setMobileNavOpen(false);
  }, [activeKey]);
  useEffect(() => {
    if (activeKey !== "invoice") {
      setInvoiceMode("list");
      resetInvoiceFlow();
    }
  }, [activeKey, resetInvoiceFlow]);
  const activeNav = useMemo(
    () => allowedNavItems.find((item) => item.key === activeKey) || allowedNavItems[0] || NAV_ITEMS[0],
    [activeKey, allowedNavItems]
  );
  const config = useMemo(() => {
    if (showImportScreen) return null;
    if (activeKey === "inventory") return inventoryConfig;
    if (activeKey === "invoice") return invoiceConfig;
    if (activeKey === "suppliers") return suppliersConfig;
    if (activeKey === "customers") return customersConfig;
    const base = SCREEN_CONFIG[activeKey];
    if (!isAdminUser && (activeKey === "settings" || activeKey === "employees")) {
      return base ? { ...base, actionLabel: null } : base;
    }
    return base;
  }, [activeKey, customersConfig, invoiceConfig, inventoryConfig, suppliersConfig, showImportScreen, isAdminUser]);

  if (printMode === "barcodes") {
    return <PrintBarcodesPage />;
  }

  if (!session) {
    return (
      <LoginScreen
        onSubmit={handleLogin}
        error={authError}
        defaultEmail={DEFAULT_LOGIN_EMAIL}
        authLoading={authLoading}
      />
    );
  }

  const headerIcon = showImportScreen ? "import" : activeNav.icon;
  const profileInitials = (session?.role || "SA")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const importMeta = { title: "Import products", helper: "Upload CSV or Excel files to create inventory items in bulk." };
  const isInvoiceBuilder = activeKey === "invoice" && invoiceMode !== "list";
  const meta = showImportScreen
    ? importMeta
    : isInvoiceBuilder
      ? { title: invoiceMode === "edit" ? "Edit Invoice" : "New Invoice", helper: "Select the customer and items to generate an invoice and PDF." }
      : activeNav.key === "employees" && !isAdminUser
        ? { title: "My profile", helper: "Update your details and request leave." }
        : SCREEN_META[activeNav.key] || SCREEN_META.home;

  if (session && !canAccess(activeKey)) {
    return (
      <div className="app-shell">
        <aside className="sidebar">
          <header className="brand">
            <div className="brand-mark" aria-hidden="true">
              <img src={highWindowLogo} alt="High Window logo" className="brand-logo" />
            </div>
          </header>
          <button
            className="primary-btn"
            type="button"
            onClick={() => {
              resetProductFlow();
              setShowCreateModal(true);
              setSelectedCreateKey(null);
              setShowProductDrawer(false);
              setShowSupplierDrawer(false);
              setShowImportScreen(false);
            }}
          >
            + Create New
          </button>
          <nav className="nav">
            {allowedNavItems.map((item) => (
              <button
                key={item.key}
                className={`nav-item ${activeKey === item.key ? "active" : ""}`}
                onClick={() => setActiveKey(item.key)}
              >
                <span className="nav-icon">
                  <Icon name={item.icon} />
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
          <div className="sidebar-footer">
            <div className="profile-card">
              <div className="avatar" aria-hidden="true">
                <span>{profileInitials}</span>
              </div>
            <div className="profile-details">
                <div className="profile-name">{session?.role || "User"}</div>
                <div className="profile-email">{session?.email}</div>
              </div>
            </div>
            <button className="ghost-btn logout-btn" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </aside>

        <main className="main">
          <header className="main-header">
            <div>
              <div className="title-line">
                <span className="panel-icon soft">
                  <Icon name="settings" />
                </span>
                <h1>No access</h1>
              </div>
              <p className="muted">This account is not allowed to view this section.</p>
            </div>
          </header>
          <BlankState title="No access" helper="Ask an admin to grant access to this tab." iconName="settings" />
        </main>
      </div>
    );
  }

  const renderPage = () => {
    if (showImportScreen) {
      return (
        <ProductImportPage
          suppliers={supplierOptions}
          onRequestSupplierCreate={handleImportSupplierRequest}
          onImported={handleProductsImported}
          onClose={() => setShowImportScreen(false)}
        />
      );
    }
    switch (activeKey) {
      case "home":
        return (
          <HomePage
            stats={homeStats}
            inventoryStats={homeInventoryStats}
            inventorySnapshot={homeInventorySnapshot}
            invoiceSnapshot={homeInvoiceSnapshot}
            alerts={homeAlerts}
            activityRows={homeActivityRows}
          />
        );
      case "inventory":
        return (
          <InventoryPage
            config={config}
            status={inventoryStatus}
            onRowClick={handleInventoryRowClick}
            onRowsUpdate={updateInventoryRows}
            onNotify={notify}
            onOpenBarcodeModal={handleOpenInventoryBarcodeModal}
          />
        );
      case "categories":
        return <CategoriesPage onNotify={notify} />;
      case "invoice":
        if (isInvoiceBuilder) {
          return (
            <InvoiceBuilderPage
              customers={customerOptions}
              products={inventoryProducts}
              onCancel={() => {
                setInvoiceMode("list");
                resetInvoiceFlow();
              }}
              onSave={handleCreateInvoice}
              onCreateCustomer={handleCreateCustomerInline}
              saving={invoiceSaving}
              error={invoiceFormError}
              savedInvoice={lastSavedInvoice}
              loading={invoiceBuilderLoading}
              mode={invoiceMode}
              canUpdateStatus={isAdminUser}
              onRecordPayment={handleRecordInvoicePayment}
              onLogEvent={handleLogInvoiceEvent}
            />
          );
        }
        return (
          <InvoicePage
            config={config}
            rows={invoicesState.rows}
            status={invoicesStatus}
            onReload={loadInvoices}
            onRowClick={handleInvoiceRowClick}
          />
        );
      case "suppliers":
        return <SuppliersPage config={config} onRowClick={handleSupplierRowClick} />;
      case "customers":
        return <CustomersPage config={config} onRowClick={handleCustomerRowClick} />;
      case "reports":
        return <ReportsPage config={config} />;
      case "employees":
        return (
          <EmployeesPage
            currentUserEmail={session?.email}
            openDrawerSignal={employeesDrawerSignal}
            onDeactivateCurrentUser={() => handleLogout("Your account is inactive. Contact an admin.")}
            isAdminUser={isAdminUser}
            canManageUsers={isAdminUser}
          />
        );
      case "settings":
        return (
          <SettingsPage
            currentUserEmail={session?.email}
            openDrawerSignal={settingsDrawerSignal}
            onDeactivateCurrentUser={() => handleLogout("Your account is inactive. Contact an admin.")}
            canManageUsers={isAdminUser}
          />
        );
      default:
        return <BlankState title={meta.title} helper={meta.helper} iconName={activeNav.icon} />;
    }
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNavOpen ? "is-open" : ""}`}>
        <header className="brand">
          <div className="brand-mark" aria-hidden="true">
            <img src={highWindowLogo} alt="High Window logo" className="brand-logo" />
          </div>
          <div className="brand-compact" aria-label="High Window admin">
            <span className="brand-compact__badge">HW</span>
            <div className="brand-compact__copy">
              <span className="brand-compact__title">High Window</span>
              <span className="brand-compact__subtitle">Admin</span>
            </div>
          </div>
        </header>

        <button
          className="primary-btn"
          type="button"
        onClick={() => {
          resetProductFlow();
          setShowCreateModal(true);
          setSelectedCreateKey(null);
          setShowProductDrawer(false);
          setShowSupplierDrawer(false);
          setShowImportScreen(false);
          setMobileNavOpen(false);
        }}
      >
        + Create New
      </button>

        <nav className="nav">
          {allowedNavItems.map((item) => (
              <button
                key={item.key}
                className={`nav-item ${activeKey === item.key ? "active" : ""}`}
                onClick={() => {
                  setActiveKey(item.key);
                  setMobileNavOpen(false);
                }}
              >
                <span className="nav-icon">
                  <Icon name={item.icon} />
              </span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="profile-card">
            <div className="avatar" aria-hidden="true">
              <span>{profileInitials}</span>
            </div>
            <div className="profile-details">
              <div className="profile-name">{session?.role || "Super Admin"}</div>
              <div className="profile-email">{session?.email}</div>
            </div>
          </div>
          <button className="ghost-btn logout-btn" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </aside>
      {mobileNavOpen ? <button className="nav-scrim" type="button" aria-label="Close menu" onClick={() => setMobileNavOpen(false)} /> : null}

      <main className="main">
        <header className="main-header">
          <div>
            <div className="title-line">
              <span className="panel-icon soft">
                <Icon name={headerIcon} />
              </span>
              <h1>{meta.title}</h1>
            </div>
            <p className="muted">{meta.helper}</p>
          </div>
          <div className="main-header-actions">
            <button
              className="icon-btn mobile-nav-toggle"
              type="button"
              aria-expanded={mobileNavOpen}
              aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              <Icon name={mobileNavOpen ? "close" : "menu"} />
            </button>
            {!showImportScreen && config?.actionLabel ? (
              <button className="primary-btn alt" type="button" onClick={handleActionButton}>
                {config.actionLabel}
              </button>
            ) : null}
          </div>
        </header>

        {renderPage()}
      </main>

      <CreateModal
        open={showCreateModal}
        selectedKey={selectedCreateKey}
        onSelect={handleSelectCreate}
        onClose={handleCloseCreate}
        onCreate={handleCreateSubmit}
        sideContent={
          <>
            <ProductDrawer
              open={showProductDrawer}
              onClose={handleCloseCreate}
              onSubmit={handleCreateProduct}
              onUpdate={handleUpdateProduct}
              saving={productSaving}
              error={productFormError}
              product={selectedProduct}
              suppliers={supplierOptions}
              mode={drawerMode}
              onOpenBarcodeModal={handleOpenProductBarcodeModal}
            />
            <SupplierDrawer
              open={showSupplierDrawer}
              onClose={handleCloseSupplierDrawer}
              onSubmit={handleCreateSupplier}
              onUpdate={handleUpdateSupplier}
              onLinkProducts={handleLinkProductsToSupplier}
              onDelinkProduct={handleDelinkProductFromSupplier}
              onToggleStatus={handleToggleSupplierStatus}
              saving={supplierSaving}
              linking={supplierLinking}
              statusChanging={supplierStatusChanging}
              error={supplierFormError}
              linkError={supplierLinkError}
              supplier={selectedSupplier}
              products={inventoryProducts}
              mode={supplierDrawerMode}
            />
            <CustomerDrawer
              open={showCustomerDrawer}
              onClose={handleCloseCreate}
              onSubmit={handleCreateCustomer}
              onUpdate={handleUpdateCustomer}
              saving={customerSaving}
              error={customerFormError}
              customer={selectedCustomer}
              mode={customerDrawerMode}
            />
          </>
        }
      />
      {!showCreateModal ? (
        <>
          <ProductDrawer
            open={showProductDrawer}
            onClose={handleCloseCreate}
            onSubmit={handleCreateProduct}
            onUpdate={handleUpdateProduct}
            saving={productSaving}
            error={productFormError}
            withOverlay
            product={selectedProduct}
            suppliers={supplierOptions}
            mode={drawerMode}
            onOpenBarcodeModal={handleOpenProductBarcodeModal}
          />
          <SupplierDrawer
            open={showSupplierDrawer}
            onClose={handleCloseSupplierDrawer}
            onSubmit={handleCreateSupplier}
            onUpdate={handleUpdateSupplier}
            onLinkProducts={handleLinkProductsToSupplier}
            onDelinkProduct={handleDelinkProductFromSupplier}
            onToggleStatus={handleToggleSupplierStatus}
            saving={supplierSaving}
            linking={supplierLinking}
            statusChanging={supplierStatusChanging}
            error={supplierFormError}
            linkError={supplierLinkError}
            withOverlay
            supplier={selectedSupplier}
            products={inventoryProducts}
            mode={supplierDrawerMode}
          />
          <CustomerDrawer
            open={showCustomerDrawer}
            onClose={handleCloseCreate}
            onSubmit={handleCreateCustomer}
            onUpdate={handleUpdateCustomer}
            saving={customerSaving}
            error={customerFormError}
            withOverlay
            customer={selectedCustomer}
            mode={customerDrawerMode}
          />
        </>
      ) : null}
      <BarcodeModal
        open={showBarcodeModal}
        onClose={closeBarcodeModal}
        rows={inventoryState.rows}
        columns={inventoryConfig?.columns || SCREEN_CONFIG.inventory.columns}
        defaultSelection={barcodeSelection}
        loading={inventoryState.loading}
      />
      {toast?.message ? (
        <div className={`toast ${toast.tone}`} role="status" aria-live="polite" onClick={clearToast}>
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}

export default App;
